import io from 'socket.io-client';
import feathers from '@feathersjs/client/dist/feathers.min.js';
import socketio from '@feathersjs/client/dist/socketio.min.js';
import auth from '@feathersjs/client/dist/authentication.min.js';
import config from '@/config';
import Vue from 'vue';

const socket = io(config.server_url);
const signageSocket = io(config.server_url, { path: '/signage' });

export const client = feathers();
export const conversionEventBus = new Vue();

let _currentUserId = null;
let _sessionHandler = null;
let _venuesUpdateHandler = null;

try {
  client
    .configure(socketio(socket, { timeout: config.socket_timeout }))
    .configure(auth({ storage: window.localStorage }));

  client.io = socket;
  client.signageSocket = signageSocket;
} catch (error) {
  console.error('Error configuring Feathers client:', error);
}

// ─── Socket lifecycle ─────────────────────────────────────────────────────────

socket.on('connect', () => setupConversionListeners());
socket.on('reconnect', () => {
  setupConversionListeners();
  reregisterSessionHandler();
});

signageSocket.on('connect', () => {
  setupDeviceLogsListeners();
  setupDownloadProgressListeners();
});
signageSocket.on('reconnect', () => {
  setupDeviceLogsListeners();
  setupDownloadProgressListeners();
});

if (socket.connected) setupConversionListeners();
if (signageSocket.connected) {
  setupDeviceLogsListeners();
  setupDownloadProgressListeners();
}

// Support Socket.IO v3+ reconnect event
if (socket.io) {
  socket.io.on('reconnect', () => reregisterSessionHandler());
}

// ─── Conversion listeners ─────────────────────────────────────────────────────

function setupConversionListeners() {
  socket.off('conversion:start');
  socket.off('conversion:progress');
  socket.off('conversion:complete');
  socket.off('conversion:error');

  socket.on('conversion:start', data => {
    conversionEventBus.$emit('conversion:start', {
      id: `conversion_${data.filename}`,
      name: `Converting ${data.originalFileName || data.filename}`,
      message: 'Converting H.265 to H.264...'
    });
  });

  socket.on('conversion:progress', data => {
    conversionEventBus.$emit('conversion:progress', {
      id: `conversion_${data.filename}`,
      progress: data.progress,
      name: data.originalFileName || data.filename
    });
  });

  socket.on('conversion:complete', data => {
    conversionEventBus.$emit('conversion:complete', {
      id: `conversion_${data.filename}`,
      message: `${data.originalFileName || data.filename} converted successfully!`
    });
  });

  socket.on('conversion:error', data => {
    conversionEventBus.$emit('conversion:error', {
      id: `conversion_${data.filename}`,
      message: `Failed to convert ${data.originalFileName || data.filename}: ${data.error}`
    });
  });
}

// ─── Device logs listeners ────────────────────────────────────────────────────

function setupDeviceLogsListeners() {
  signageSocket.off('device-logs:requesting');
  signageSocket.off('device-logs:ready');
  signageSocket.off('device-logs:error');

  signageSocket.on('device-logs:requesting', data => {
    import('@/store').then(({ default: store }) => {
      store.commit('devices/SET_DOWNLOADING_LOGS', {
        deviceId: data.deviceId,
        isDownloading: true
      });
    });
  });

  signageSocket.on('device-logs:ready', data => {
    import('@/store').then(({ default: store }) => {
      store.commit('devices/SET_DOWNLOADING_LOGS', {
        deviceId: data.deviceId,
        isDownloading: false
      });
    });

    let downloadUrl = data.downloadUrl;
    if (!downloadUrl.startsWith('http')) {
      downloadUrl = window.location.origin + downloadUrl;
    }

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = data.filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => document.body.removeChild(link), 100);

    conversionEventBus.$emit('show-notification', {
      type: 'success',
      message: `Logs from ${data.deviceName} downloaded successfully!`
    });
  });

  signageSocket.on('device-logs:error', data => {
    import('@/store').then(({ default: store }) => {
      store.commit('devices/SET_DOWNLOADING_LOGS', {
        deviceId: data.deviceId,
        isDownloading: false
      });
    });

    conversionEventBus.$emit('show-notification', {
      type: 'error',
      message: data.error || 'Failed to download logs'
    });
  });
}

// ─── Download progress listeners ──────────────────────────────────────────────

function setupDownloadProgressListeners() {
  signageSocket.off('device-download-progress');
  signageSocket.off('active-downloads-snapshot');

  signageSocket.on('active-downloads-snapshot', snapshot => {
    if (!snapshot || typeof snapshot !== 'object') return;
    import('@/store').then(({ default: store }) => {
      // read directly from state, avoid getter calling getSelectedVenue()
      const devices = store.state.devices.devices;

      Object.values(snapshot).forEach(info => {
        const device = devices.find(d => d.serial === info.deviceId);
        if (!device) return;
        store.commit('devices/SET_DEVICE_DOWNLOAD', {
          deviceId: device._id,
          downloadState: {
            serial: info.deviceId,
            totalSize: info.totalSize || 0,
            items: (info.items || []).map(i => ({
              filename: i.filename,
              size: i.size || 0,
              downloaded: i.downloaded || 0,
              total: i.size || 0,
              progress: i.progress || 0,
              status: i.status || 'pending'
            })),
            overallProgress: info.overallProgress || 0,
            status: 'downloading',
            startTime: Date.now(),
            lastUpdate: Date.now(),
            speed: 0
          }
        });
      });
    });
  });

  signageSocket.on('device-download-progress', data => {
    import('@/store').then(({ default: store }) => {
      // read directly from state, avoid getter calling getSelectedVenue()
      const devices = store.state.devices.devices;
      const device =
        devices.find(d => d.serial === data.deviceId) ||
        store.state.devices.paginatedDevices.find(d => d.serial === data.deviceId);

      if (!device) return;

      handleDownloadEvent(store, data, device);
    });
  });

  signageSocket.emit('get-active-downloads');
}

function handleDownloadEvent(store, data, device) {
  const downloads = store.getters['devices/GET_DEVICE_DOWNLOADS'];

  switch (data.event) {
    case 'started': {
      store.commit('devices/SET_DEVICE_DOWNLOAD', {
        deviceId: device._id,
        downloadState: {
          serial: data.deviceId,
          totalSize: data.totalSize || 0,
          items: (data.items || []).map(i => ({
            filename: i.filename,
            size: parseInt(i.size) || 0,
            downloaded: 0,
            total: parseInt(i.size) || 0,
            progress: 0,
            status: 'pending'
          })),
          overallProgress: 0,
          status: 'downloading',
          startTime: Date.now(),
          lastUpdate: Date.now(),
          speed: 0
        }
      });
      break;
    }
    case 'progress': {
      const current = downloads[device._id];
      if (!current) return;
      const cloned = { ...current, items: [...current.items] };
      const idx = cloned.items.findIndex(i => i.filename === data.filename);
      if (idx !== -1) {
        const item = { ...cloned.items[idx] };
        const incoming = parseInt(data.downloaded) || 0;
        if (incoming < item.downloaded) return;
        const delta = incoming - item.downloaded;
        const dt = (Date.now() - cloned.lastUpdate) / 1000;
        if (dt > 0 && delta > 0) cloned.speed = delta / dt;
        item.downloaded = incoming;
        item.total = parseInt(data.total) || item.total;
        item.progress = parseFloat(data.percentage) || 0;
        item.status = 'downloading';
        cloned.items[idx] = item;
      }
      if (data.overallProgress !== undefined)
        cloned.overallProgress = Math.max(cloned.overallProgress || 0, data.overallProgress);
      cloned.lastUpdate = Date.now();
      const last = store.getters['devices/GET_DOWNLOAD_THROTTLE'](device._id);
      const now = Date.now();
      if (now - last > 300) {
        store.commit('devices/SET_DOWNLOAD_THROTTLE', { deviceId: device._id, timestamp: now });
        store.commit('devices/UPDATE_DEVICE_DOWNLOAD_ITEM', { deviceId: device._id, cloned });
      }
      break;
    }
    case 'extracting':
    case 'item-completed': {
      const current = downloads[device._id];
      if (!current) return;
      const cloned = { ...current, items: [...current.items] };
      const idx = cloned.items.findIndex(i => i.filename === data.filename);
      if (idx !== -1) {
        cloned.items[idx] = {
          ...cloned.items[idx],
          status: data.event === 'extracting' ? 'extracting' : 'completed',
          progress: 100,
          downloaded: cloned.items[idx].size || cloned.items[idx].total
        };
      }
      if (data.overallProgress !== undefined)
        cloned.overallProgress = Math.max(cloned.overallProgress || 0, data.overallProgress);
      store.commit('devices/UPDATE_DEVICE_DOWNLOAD_ITEM', { deviceId: device._id, cloned });
      break;
    }
    case 'completed': {
      const current = downloads[device._id];
      if (!current) return;
      store.commit('devices/UPDATE_DEVICE_DOWNLOAD_ITEM', {
        deviceId: device._id,
        cloned: {
          ...current,
          status: 'completed',
          overallProgress: 100,
          items: current.items.map(i => ({
            ...i,
            status: 'completed',
            progress: 100,
            downloaded: i.size || i.total
          }))
        }
      });
      setTimeout(() => {
        store.commit('devices/DELETE_DEVICE_DOWNLOAD', device._id);
        store.commit('devices/CLEAR_DOWNLOAD_THROTTLE', device._id);
      }, 5000);
      break;
    }
    case 'error': {
      store.commit('devices/DELETE_DEVICE_DOWNLOAD', device._id);
      store.commit('devices/CLEAR_DOWNLOAD_THROTTLE', device._id);
      break;
    }
  }
}

// ─── Session invalidation handler ────────────────────────────────────────────

function reregisterSessionHandler() {
  if (_currentUserId && _sessionHandler) {
    socket.off('user-session-invalidated', _sessionHandler);
    socket.on('user-session-invalidated', _sessionHandler);
  }
}

export function registerSessionInvalidationHandler(userId, onInvalidated) {
  _currentUserId = String(userId);

  // Remove previous handler if any
  if (_sessionHandler) {
    socket.off('user-session-invalidated', _sessionHandler);
  }

  _sessionHandler = function(data) {
    if (String(data.userId) === _currentUserId) {
      onInvalidated(data);
    }
  };

  socket.on('user-session-invalidated', _sessionHandler);
}

export function registerVenuesUpdatedHandler(userId, onUpdated) {
  if (_venuesUpdateHandler) {
    socket.off('user-venues-updated', _venuesUpdateHandler);
  }

  _venuesUpdateHandler = function(data) {
    if (String(data.userId) === String(userId)) {
      onUpdated(data);
    }
  };

  socket.on('user-venues-updated', _venuesUpdateHandler);
}

// ─── Exports ──────────────────────────────────────────────────────────────────

export function getSignageSocketId() {
  return signageSocket && signageSocket.id ? signageSocket.id : null;
}

export { socket, signageSocket };



// WEBPACK FOOTER //
// ./src/services/index.js